import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { categoryMeta } from "@/mocks/anime";

const navItems = [
  { to: "/", label: "首页", exact: true },
  { to: "/category/guoman", label: "动漫分类" },
  { to: "/news", label: "动漫资讯" },
  { to: "/about", label: "关于我们" },
];

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setCategoryOpen(false);
  }, [location.pathname, location.search]);

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur border-background-200"
          : "bg-background-50 border-background-100"
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex h-16 md:h-[72px] items-center gap-4 lg:gap-8">
          <Link to="/" className="flex items-center gap-2 shrink-0 cursor-pointer">
            <span className="w-9 h-9 rounded-md bg-primary-900 text-accent-400 flex items-center justify-center">
              <i className="ri-sparkling-2-fill text-xl leading-none" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="text-[17px] font-black tracking-tight text-foreground-950">妖精动漫</span>
              <span className="text-[10px] font-semibold tracking-[0.18em] text-foreground-400 mt-1">FAIRY ANIME</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1" aria-label="主导航">
            {navItems.map((item) =>
              item.label === "动漫分类" ? (
                <div
                  key={item.to}
                  className="relative"
                  onMouseEnter={() => setCategoryOpen(true)}
                  onMouseLeave={() => setCategoryOpen(false)}
                >
                  <NavLink
                    to={item.to}
                    className={({ isActive }) =>
                      `px-3 py-2 rounded-md text-[14px] font-medium transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap ${
                        isActive || location.pathname.startsWith("/category") || location.pathname.startsWith("/detail")
                          ? "text-primary-700 bg-primary-50"
                          : "text-foreground-700 hover:text-foreground-950 hover:bg-background-100"
                      }`
                    }
                  >
                    动漫分类
                    <i className={`ri-arrow-down-s-line text-base transition-transform ${categoryOpen ? "rotate-180" : ""}`} />
                  </NavLink>
                  {categoryOpen && (
                    <div className="absolute left-0 top-full pt-2 w-[420px]">
                      <div className="bg-background-50 border border-background-200 rounded-lg p-2 grid grid-cols-2 gap-1">
                        {categoryMeta.map((cat) => (
                          <Link
                            key={cat.slug}
                            to={`/category/${cat.slug}`}
                            className="flex items-start gap-2 px-2.5 py-2 rounded-md hover:bg-primary-50 transition-colors cursor-pointer"
                          >
                            <span className="w-7 h-7 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center shrink-0 mt-0.5">
                              <i className={`${cat.icon} text-sm`} />
                            </span>
                            <span className="flex flex-col">
                              <span className="text-[13px] font-semibold text-foreground-900">{cat.name}</span>
                              <span className="text-[11px] text-foreground-500 mt-0.5 line-clamp-1">{cat.desc}</span>
                            </span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.exact}
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-md text-[14px] font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      isActive
                        ? "text-primary-700 bg-primary-50"
                        : "text-foreground-700 hover:text-foreground-950 hover:bg-background-100"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              )
            )}
          </nav>

          <div className="flex-1" />

          <form onSubmit={onSearch} className="hidden md:flex items-center h-9 w-[210px] lg:w-[280px] rounded-full bg-background-100 border border-background-200 px-3 gap-2">
            <span className="w-4 h-4 flex items-center justify-center text-foreground-400">
              <i className="ri-search-line text-sm" />
            </span>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索动漫 / 资讯"
              className="flex-1 min-w-0 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
              aria-label="搜索动漫或资讯"
            />
            <button type="submit" className="text-[11px] font-semibold text-accent-600 hover:text-accent-700 whitespace-nowrap cursor-pointer">
              搜索
            </button>
          </form>

          <Link
            to="/search"
            className="hidden md:flex w-9 h-9 items-center justify-center rounded-full bg-primary-900 text-background-50 hover:bg-primary-800 transition-colors cursor-pointer"
            aria-label="打开搜索页"
          >
            <i className="ri-compass-3-line text-base" />
          </Link>

          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="打开菜单"
            aria-expanded={mobileOpen}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-md bg-background-100 text-foreground-900 cursor-pointer"
          >
            <i className={mobileOpen ? "ri-close-line text-xl" : "ri-menu-3-line text-xl"} />
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 py-4 animate-fade-up">
          <form onSubmit={onSearch} className="flex items-center h-10 rounded-full bg-background-100 border border-background-200 px-3 gap-2 mb-4">
            <span className="w-4 h-4 flex items-center justify-center text-foreground-400">
              <i className="ri-search-line text-sm" />
            </span>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索动漫 / 资讯"
              className="flex-1 min-w-0 bg-transparent text-sm outline-none placeholder:text-foreground-400"
              aria-label="搜索动漫或资讯"
            />
            <button type="submit" className="text-xs font-semibold text-accent-600 whitespace-nowrap cursor-pointer">
              搜索
            </button>
          </form>
          <div className="flex flex-col">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.exact}
                className="px-2 py-3 text-[15px] font-medium text-foreground-800 border-b border-background-100 last:border-0 cursor-pointer"
              >
                {item.label}
              </NavLink>
            ))}
            <Link to="/search" className="px-2 py-3 text-[15px] font-medium text-foreground-800 cursor-pointer">
              搜索结果
            </Link>
          </div>
          <div className="mt-4">
            <p className="text-[11px] font-semibold tracking-widest text-foreground-400 mb-2">热门分类</p>
            <div className="grid grid-cols-2 gap-2">
              {categoryMeta.slice(0, 6).map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex items-center gap-2 px-2.5 py-2 rounded-md bg-background-100 text-[13px] font-medium text-foreground-800 cursor-pointer"
                >
                  <i className={`${cat.icon} text-primary-600`} />
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}